"use client";

import React, { useState, useTransition } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import type { SignUpSchema } from "@gsa/db/schema";
import { signUp } from "@gsa/auth/actions";
import { signUpSchema } from "@gsa/db/schema";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@gsa/ui/card";
import { LoadingButton } from "@gsa/ui/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@gsa/ui/components/ui/form";
import { AlertCircleIcon, ChevronRight } from "@gsa/ui/icons";
import { Input } from "@gsa/ui/input";

export default function SignUpForm() {
  const router = useRouter();

  const [pending, startTransaction] = useTransition();
  const [error, setError] = useState<string | undefined>();

  const form = useForm<SignUpSchema>({
    resolver: zodResolver(signUpSchema),

    defaultValues: {},
  });

  const onSubmit = async (input: SignUpSchema) => {
    startTransaction(async () => {
      const result = await signUp(input);

      if (result?.ok === false) {
        setError(result?.message ?? "Failed to sign up");
      } else {
        setError("");
        router.push("/account/profile");
      }
    });
  };

  return (
    <Form {...form}>
      <div className=" mx-auto w-full md:max-w-md  ">
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="relative grid gap-8  "
        >
          <Card>
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl">Create An Account</CardTitle>
              <CardDescription>
                Enter your email & password below to create an account
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 ">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Name <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="text"
                        required
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Email Address <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="email"
                        autoComplete="email"
                        required
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Password <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        type="password"
                        required
                        autoComplete="new-password"
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {error && (
                <div
                  className="flex h-8 items-end space-x-1"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <AlertCircleIcon className="h-5 w-5 text-red-500" />
                  <p className="text-sm text-red-500">{error}</p>
                </div>
              )}
            </CardContent>

            <CardFooter>
              <LoadingButton
                loading={pending}
                disabled={pending}
                className="w-full justify-between"
              >
                Create My Account <ChevronRight />
              </LoadingButton>
            </CardFooter>
          </Card>

          <div className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link href="/account/login">
              <span className="text-green-500">Login here</span>
            </Link>
            .
          </div>
        </form>
      </div>
    </Form>
  );
}
